import type { OnlineCourseKitLine, OnlineCourseOption } from "@/lib/online-course-pricing";
import { kitPricePence } from "@/lib/online-course-kit-selection";

export type ConfigManagedBasketLine = {
  configurationId: string;
  optionCode: string;
  courseSlug: string;
  courseTitle: string;
  pricePence: number;
  kitSelections?: OnlineCourseKitLine[];
};

export type LegacyBasketLine = {
  priceId: string;
  pricePence: number;
  courseSlug: string;
  courseTitle: string;
};

export function isConfigManagedBasketEntry(course: {
  fields?: {
    configurationId?: string | null;
    optionsManaged?: boolean;
  };
}): boolean {
  return Boolean(course?.fields?.configurationId);
}

export function resolveOnlineBasketPricePence(course: {
  fields?: {
    pricing?: { unit_amount?: number } | null;
    data?: {
      price?: string | number | null;
      full?: string | number | null;
    } | null;
  };
  event?: { standardPrice?: number | null };
}) {
  if (course.fields?.pricing?.unit_amount) {
    return course.fields.pricing.unit_amount;
  }

  const dataPrice = course.fields?.data?.price;
  if (dataPrice !== null && dataPrice !== undefined && dataPrice !== "") {
    return Math.round(Number(dataPrice) * 100);
  }

  if (course.event?.standardPrice) {
    return course.event.standardPrice;
  }

  const full = course.fields?.data?.full;
  if (full !== null && full !== undefined && full !== "") {
    const parsed = parseFloat(String(full));
    if (Number.isFinite(parsed)) {
      return parsed < 1000 ? Math.round(parsed * 100) : Math.round(parsed);
    }
  }

  return 0;
}

export function normalizeOnlineListingImageUrl(imageUrl?: string | null) {
  if (!imageUrl) {
    return "/logo/cc-logo.svg";
  }

  if (imageUrl.startsWith("//")) {
    return imageUrl;
  }

  if (imageUrl.startsWith("https://") || imageUrl.startsWith("http://")) {
    return imageUrl.replace(/^https?:/, "");
  }

  return imageUrl;
}

export function buildOnlineListingImage(title: string, imageUrl?: string | null) {
  return {
    fields: {
      title,
      file: {
        url: normalizeOnlineListingImageUrl(imageUrl),
      },
    },
  };
}

export function buildOnlineConfigBasketEntry({
  title,
  url,
  eventDetailId,
  configurationId,
  optionCode,
  pricePence,
  wasPricePence,
  metadata,
  type,
  listingImageUrl,
  bogofEligible,
}: {
  title: string;
  url: string;
  eventDetailId: string;
  configurationId?: string | null;
  optionCode?: string | null;
  pricePence: number;
  wasPricePence?: number | null;
  metadata?: Record<string, unknown> | null;
  type?: string | null;
  listingImageUrl?: string | null;
  bogofEligible?: boolean;
}) {
  const configId = configurationId || eventDetailId;
  const price = pricePence || 0;

  return {
    sys: { id: configId },
    fields: {
      title,
      url,
      categories: ["elearning", "online-courses"],
      listingImage: buildOnlineListingImage(title, listingImageUrl),
      optionsManaged: true,
      configurationId: configId,
      optionCode: optionCode || null,
      bogofEligible: bogofEligible ?? false,
      pricing: {
        id: configId,
        unit_amount: price,
      },
      data: {
        full: (price / 100).toFixed(2),
        price: (price / 100).toFixed(2),
        was: wasPricePence ? (wasPricePence / 100).toFixed(2) : null,
      },
    },
    event: {
      id: eventDetailId,
      title,
      type,
      metadata,
      standardPrice: price,
      events: [],
    },
    selectedEvent: null,
    online: true,
  };
}

export function buildCheckoutPayload(
  courses: Array<{
    fields: {
      configurationId?: string | null;
      optionCode?: string | null;
      kitSelections?: OnlineCourseKitLine[];
      title?: string;
      url?: string;
      pricing?: { id?: string; unit_amount?: number } | null;
    };
  }>,
  additional: string[] = [],
  mapping: Record<string, { price_data: { unit_amount: number } }> = {},
) {
  const configurationItems: ConfigManagedBasketLine[] = [];
  const priceIds: string[] = [];

  courses.forEach((course) => {
    if (course.fields.configurationId) {
      configurationItems.push({
        configurationId: course.fields.configurationId,
        optionCode: course.fields.optionCode || "",
        courseSlug:
          course.fields.url?.split("/").filter(Boolean).slice(-1)[0] || "",
        courseTitle: course.fields.title || "",
        pricePence: course.fields.pricing?.unit_amount || 0,
        kitSelections: course.fields.kitSelections || [],
      });
      return;
    }

    if (course.fields.pricing?.id) {
      priceIds.push(course.fields.pricing.id);
    }
  });

  const additionalLineItems =
    additional.length > 0
      ? additional
          .filter((id) => mapping[id])
          .map((id) => mapping[id].price_data)
      : [];

  return {
    configurationItems,
    priceIds,
    additional: additionalLineItems,
    metadata: {
      purchase_flow: "basket",
      configuration_ids: configurationItems
        .map((item) => item.configurationId)
        .join(","),
    },
  };
}

export function basketKitTotalPence(kitSelections: OnlineCourseKitLine[] = []) {
  return kitSelections.reduce(
    (sum, kit) => sum + kitPricePence(kit) * (kit.quantity || 1),
    0,
  );
}

export function attachDefaultOptionToLegacyEntry<T extends Record<string, unknown>>(
  entry: T,
  option: OnlineCourseOption,
  kitSelections: OnlineCourseKitLine[] = [],
): T {
  const fields = (entry.fields || {}) as Record<string, unknown>;

  return {
    ...entry,
    fields: {
      ...fields,
      optionsManaged: true,
      configurationId: option.id,
      optionCode: option.option_code,
      optionLabel: option.option_label,
      bogofEligible: Boolean(fields.bogofEligible),
      requiredKits: option.required_kits || [],
      kitSelections,
      pricing: {
        id: option.id,
        unit_amount: option.price_pence,
      },
      data: {
        ...(fields.data as Record<string, unknown>),
        priceId: option.id,
        full: (option.price_pence / 100).toFixed(2),
        price: (option.price_pence / 100).toFixed(2),
        was: option.was_price_pence
          ? (option.was_price_pence / 100).toFixed(2)
          : (fields.data as { was?: string | null })?.was ?? null,
      },
    },
  } as T;
}

export function basketLineTotalPence(course: {
  fields: {
    pricing?: { unit_amount?: number } | null;
    kitSelections?: OnlineCourseKitLine[];
  };
}) {
  return (
    (course.fields.pricing?.unit_amount || 0) +
    basketKitTotalPence(course.fields.kitSelections || [])
  );
}

export function courseSlugFromBasketUrl(url = "") {
  return url.replace(/\/+$/, "").split("/").filter(Boolean).slice(-1)[0] || "";
}

export function basketWasPricePence(was: unknown): number | null {
  if (was === null || was === undefined || was === "") {
    return null;
  }

  if (typeof was === "number" && Number.isFinite(was)) {
    return was;
  }

  const parsed = parseFloat(String(was));
  if (!Number.isFinite(parsed)) {
    return null;
  }

  if (String(was).includes(".") || parsed < 1000) {
    return Math.round(parsed * 100);
  }

  return parsed;
}

export function buildOptionBasketFieldUpdates(
  option: OnlineCourseOption,
  kitSelections: OnlineCourseKitLine[] = [],
  existingData: Record<string, unknown> = {},
) {
  return {
    optionsManaged: true,
    configurationId: option.id,
    optionCode: option.option_code,
    optionLabel: option.option_label,
    requiredKits: option.required_kits || [],
    kitSelections,
    pricing: {
      id: option.id,
      unit_amount: option.price_pence,
    },
    data: {
      ...existingData,
      priceId: option.id,
      full: (option.price_pence / 100).toFixed(2),
      price: (option.price_pence / 100).toFixed(2),
      was: option.was_price_pence
        ? (option.was_price_pence / 100).toFixed(2)
        : (existingData.was as string | null | undefined) ?? null,
    },
  };
}

export async function fetchDefaultOptionForSlug(slug: string) {
  const response = await fetch(`/api/online-courses/${slug}/options`);
  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  if (!data.options_enabled || !Array.isArray(data.options)) {
    return null;
  }

  return (
    data.options.find((option: OnlineCourseOption) => option.is_default) ||
    data.options[0] ||
    null
  );
}
